import OrderAddressText from '../../../Component/UI/OrderAddressText';
import OrderActionToast from '../OrderList/Component/OrderActionToast';
import OrderClaimDialog from '../../../Component/UI/OrderClaims';
import BankRefundDialog from '../OrderList/Modal/BankRefundDialog';
import CancellationReasonField from '../../../Component/UI/CancellationReasonField';
import ModalCloseButton from '../../../Component/UI/ModalCloseButton';
import { sellerTable } from "../../../Component/style/sellerTable";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import api from "../../../Component/axios_Interceptor/api";
import { SERVER_ADDRESS } from "../../../Component/server";

interface Option { seq: number; option1: string; option2: string; product_code: string | null; current_cnt: number; }
interface Product { seq: number; product_num: string; name: string; price: number; option_check: string; current_cnt: number; options: Option[]; }
interface Detail {
  seq: number; product_seq: number; product_num: number; product_name: string; product_price: number;
  option_seq: number | null; option_name: string | null; order_cnt: number; order_price: number; order_status: string;
  can_return: boolean; can_refund: boolean; allowed_statuses: string[];
  detail_order_dt: string; courier: string | null; tracking_number: string | null;
}
interface Order {
  seq: number; order_dt: string; order_nickname: string; order_name: string; addr_recipient: string; addr_phone: string;
  addr_num: string; addr_basic: string; addr_detail: string; request_memo: string | null; delivery_price: number; total_payment: number;
}
interface Response { order: Order; details: Detail[]; products: Product[]; can_edit: boolean; can_edit_items: boolean; can_change_stock: boolean; stock_locked_reason: string | null; revision: string; }
interface Props { initialSelectedDetails?: number[]; ticketSeq: number; orderSeq: number; onClose: () => void; onSaved: () => void; }
const statuses = ["결제완료", "발주완료", "배송중", "배송완료", "주문취소", "사고처리"];
const money = (amount: number) => Number(amount).toLocaleString("ko-KR");
const errorMessage = (error: any) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message) ? message.join(" ") : message || "요청을 처리하지 못했습니다. 다시 시도해주세요.";
};

export default function InquiryOrderModal({ ticketSeq, orderSeq, onClose, onSaved, initialSelectedDetails = [] }: Props) {
  const [data, setData] = useState<Response | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const initialSelection = useRef(initialSelectedDetails);
  const selectionInitialized = useRef(false);
  const [claimAction, setClaimAction] = useState<{ kind: 'return' | 'refund'; details: number[] } | null>(null);
  const [bankRefundOpen, setBankRefundOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const cancelRequest = useRef('');
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [notice, setNotice] = useState("");
  const dismissNotice = useCallback(() => setNotice(""), []);
  const dialog = useRef<HTMLDialogElement>(null);
  const submitting = useRef(false);
  const request = useRef(0);
  const pendingSavedRefresh = useRef(false);
  const scope = { conversation_seq: ticketSeq, order_seq: orderSeq };
  const order = data?.order;
  const details = data?.details ?? [];
  const close = () => { if (!submitting.current) onClose(); };

  const reload = useCallback(async (refresh = false) => {
    const current = ++request.current;
    setLoading(true);
    try {
      const response = await api.get(`${SERVER_ADDRESS}orders/inquiry-edit`, { params: { conversation_seq: ticketSeq, order_seq: orderSeq } });
      if (current !== request.current) return false;
      setData(response.data);
      setSelected(selectionInitialized.current ? [] : initialSelection.current.filter(seq => response.data.details.some((row: Detail) => row.seq === seq))); selectionInitialized.current = true; setRefreshError("");
      return true;
    } catch (cause) {
      if (current === request.current) {
        if (refresh) setRefreshError(`최신 주문 정보를 불러오지 못했습니다. 다시 불러온 뒤 변경을 계속해주세요. ${errorMessage(cause)}`);
        else setError(errorMessage(cause));
      }
      return false;
    } finally {
      if (current === request.current) setLoading(false);
    }
  }, [ticketSeq, orderSeq]);

  const refreshSaved = async () => {
    if (await reload(true) && pendingSavedRefresh.current) {
      pendingSavedRefresh.current = false;
      onSaved();
    }
  };

  useEffect(() => {
    void reload();
    return () => { request.current += 1; };
  }, [reload]);
  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    element?.showModal();
    return () => { element?.close(); document.body.style.overflow = overflow; previous?.focus(); };
  }, []);

  const selectedRows = details.filter(detail => selected.includes(detail.seq));
  const canCancelSelected = !!data?.can_edit && selectedRows.length > 0 && selectedRows.every(row => row.order_status === '결제대기');
  const terminalStatuses = ['주문취소', '환불처리', '사고처리'];
  const canChangeStatus = (status: string) => {
    if (!selectedRows.length || new Set(selectedRows.map(row => row.order_status)).size !== 1
      || selectedRows.some(row => row.order_status === status || !row.allowed_statuses?.includes(status))) return false;
    if (status === '주문취소') return canCancelSelected;
    if (status === '사고처리') return !!data?.can_edit && !!data?.can_change_stock;
    return true;
  };
  const canOpenClaim = (kind: 'return' | 'refund') => selectedRows.length > 0 && selectedRows.every(row =>
    !['결제대기', ...terminalStatuses].includes(row.order_status)
    && (kind === 'return' ? row.order_status === '배송완료' && row.can_return : row.can_refund));
  const openClaim = (kind: 'return' | 'refund') => {
    if (busy || refreshError || !canOpenClaim(kind)) return;
    setError(''); setClaimAction({ kind, details: [...selected] });
  };
  const claimChanged = () => { pendingSavedRefresh.current = true; void refreshSaved(); };
  const changeStatus = async (change_status: string, confirmedCancel = false) => {
    if (submitting.current || !data || refreshError || !canChangeStatus(change_status)) return;
    setError(""); setNotice("");
    const rows = details.filter(detail => selected.includes(detail.seq));
    if (!rows.length) { setError("상태를 변경할 상품을 선택해주세요."); return; }
    if (new Set(rows.map(row => row.order_status)).size !== 1) { setError("현재 주문상태가 같은 상품끼리 선택해주세요."); return; }
    if (rows[0].order_status === change_status) { setError("이미 선택한 주문상태입니다."); return; }
    if (change_status === '주문취소') {
      if (!canCancelSelected) { setError('결제대기 상품만 주문 취소할 수 있습니다. 결제된 상품은 반품·환불처리를 이용해주세요.'); return; }
      if (!confirmedCancel) { cancelRequest.current = crypto.randomUUID(); setCancelReason(''); setCancelOpen(true); return; }
      if (!cancelReason.trim()) return;
    } else if (!window.confirm(`선택한 ${rows.length}개 상품을 ${change_status} 상태로 변경하시겠습니까?`)) return;
    submitting.current = true; setBusy(true);
    try {
      const response = await api.patch(`${SERVER_ADDRESS}orders/inquiry-status`, {
        ...scope, detail_seq_list: selected, current_status: rows[0].order_status, change_status,
        ...(change_status === "주문취소" ? {reason:cancelReason.trim(),request_id:cancelRequest.current} : {}),
      }, { headers: { "Content-Type": "application/json" } });
      if (response.data.fail) setError(response.data.failures?.map((failure: { message: string }) => failure.message).join(" ") || "일부 상품의 상태를 변경하지 못했습니다.");
      if (Number(response.data.success) > 0) {
        setNotice(`${response.data.success}개 상품의 주문상태가 변경되었습니다.`);
        pendingSavedRefresh.current = true;
      }
      if (!response.data.fail) setCancelOpen(false);
      await refreshSaved();
    } catch (cause) { setError(errorMessage(cause)); }
    finally { submitting.current = false; setBusy(false); }
  };

  return <><Dialog data-admin-modal="" ref={dialog} aria-labelledby="inquiry-order-title" onCancel={event => { event.preventDefault(); close(); }}>
    <Header data-admin-modal-header=""><h2 id="inquiry-order-title">주문 상태 변경</h2><ModalCloseButton tone="admin" aria-label="주문 상태 변경 닫기" disabled={busy} onClick={close} /></Header>
    <Body data-admin-modal-body="" aria-busy={loading || busy}>
      <p className="help">상품을 선택해 주문 상태를 변경해주세요. 주문 취소는 결제대기 상품만 가능합니다.</p>
      {error && <p className="error" role="alert">{error}</p>}
      {refreshError && <div className="error" role="alert"><p>{refreshError}</p><button type="button" disabled={busy || loading} onClick={() => void refreshSaved()}>최신 주문 다시 불러오기</button></div>}
      <OrderActionToast message={notice} onDismiss={dismissNotice} />
      {loading ? <p className="empty" role="status">주문 정보를 불러오는 중입니다.</p> : !data || !order ? <div className="empty"><p>주문 정보를 불러오지 못했습니다.</p><button type="button" onClick={() => { setError(""); void reload(); }}>다시 시도</button></div> : <>
        {data.stock_locked_reason && <p className="locked">{data.stock_locked_reason}</p>}
        <Actions><strong>선택 상품 {selected.length}개</strong>{statuses.map(status => <StatusButton type="button" key={status} data-tone={status === "주문취소" || status === "사고처리" ? "danger" : "brand"} disabled={busy || !!refreshError || !canChangeStatus(status)} onClick={() => void changeStatus(status)}>{status}</StatusButton>)}<StatusButton type="button" data-tone="warning" disabled={busy || !!refreshError || !canOpenClaim('return')} onClick={() => openClaim('return')}>반품 접수</StatusButton><StatusButton type="button" data-tone="danger" disabled={busy || !!refreshError || !canOpenClaim('refund')} onClick={() => openClaim('refund')}>환불처리</StatusButton></Actions>
        {cancelOpen && <CancelReasonBox aria-label="관리자 주문 취소">
          <CancellationReasonField value={cancelReason} onChange={setCancelReason} disabled={busy} />
          <Actions><button type="button" disabled={busy} onClick={() => setCancelOpen(false)}>닫기</button>
            <button type="button" disabled={busy || !cancelReason.trim() || !selected.length} onClick={() => void changeStatus('주문취소', true)}>취소하기</button></Actions>
        </CancelReasonBox>}
        <Scroll tabIndex={0} aria-label="주문 상품 및 배송정보 표, 가로 스크롤 가능"><Table>
          <thead><tr><th><input type="checkbox" aria-label="주문 상품 전체 선택" checked={details.length > 0 && selected.length === details.length} disabled={busy || !details.length} onChange={event => setSelected(event.target.checked ? details.map(detail => detail.seq) : [])} /></th>{["주문일시", "주문자", "입금자", "상품코드", "품목명", "옵션", "단가", "수량", "총금액", "주문상태", "수령인", "연락처", "우편번호", "기본주소", "상세주소", "배송 요청사항", "택배사", "송장번호"].map(label => <th key={label}>{label}</th>)}</tr></thead>
          <tbody>{details.map((detail, index) => {
            const registered = data.products.find(item => item.seq === detail.product_seq);
            const selectedOption = registered?.options.find(option => option.seq === detail.option_seq);
            return <tr key={detail.seq}>
              <td><input type="checkbox" aria-label={`${detail.product_name} 선택`} checked={selected.includes(detail.seq)} disabled={busy} onChange={event => setSelected(items => event.target.checked ? [...items, detail.seq] : items.filter(seq => seq !== detail.seq))} /></td>
              <td>{new Date(detail.detail_order_dt || order.order_dt).toLocaleString("ko-KR")}</td><td>{order.order_nickname || "미등록"}</td>
              {index === 0 && <td rowSpan={details.length}>{order.order_name || "미등록"}</td>}
              <td>{selectedOption?.product_code || "미등록"}</td><td className="product">{detail.product_name}</td>
              <td>{detail.option_name || "옵션 없음"}</td>
              <td className="number">{money(detail.product_price)}</td><td>{detail.order_cnt}</td><td className="number">{money(detail.product_price * detail.order_cnt)}</td><td>{detail.order_status}</td>
              {(['addr_recipient', 'addr_phone', 'addr_num', 'addr_basic', 'addr_detail', 'request_memo'] as const).map(key => <td key={key}><ReadOnlyText $wide={key === 'addr_basic' || key === 'addr_detail' || key === 'request_memo'}><OrderAddressText address={order[key] || "미등록"} /></ReadOnlyText></td>)}
              <td>{detail.courier || "미등록"}</td><td>{detail.tracking_number || "미등록"}</td>
            </tr>;
          })}</tbody>
        </Table></Scroll>
        <Summary>상품금액 {money(order.total_payment)}원 <span>배송비 {money(order.delivery_price)}원</span><strong>총 주문금액 {money(Number(order.total_payment) + Number(order.delivery_price))}원</strong></Summary>
      </>}
    </Body><Footer data-admin-modal-footer=""><button type="button" className="complete" disabled={busy} onClick={close}>닫기</button></Footer>
  </Dialog>{claimAction && <OrderClaimDialog admin initialView="request" orderSeq={orderSeq} initialKind={claimAction.kind} initialDetailSeqs={claimAction.details}
    onClose={() => setClaimAction(null)} onChanged={claimChanged} onBankRefund={() => { setClaimAction(null); setBankRefundOpen(true); }} />}
    {bankRefundOpen && <BankRefundDialog orderSeq={orderSeq} onClose={() => setBankRefundOpen(false)} onConfirmed={async () => { pendingSavedRefresh.current = true; await refreshSaved(); }} />}</>;
}

const Dialog = styled.dialog`
  box-sizing: border-box; width: min(1580px, calc(100vw - 64px)); max-width: none; max-height: calc(100dvh - 64px);
  padding: 0; border: 0; border-radius: 8px; color: var(--admin-theme-text, #292929); background: var(--admin-theme-surface, #fff); overflow: hidden;
  font-family: var(--font-family-base), sans-serif; font-weight: var(--font-weight-regular); box-shadow: 0 18px 60px #0003;
  &::backdrop { background: #0007; }
  button, input, select { font: inherit; }
  button { min-height: 34px; padding: 7px 14px; border: 1px solid var(--admin-theme-border, #d4d4d4); border-radius: 4px; color: var(--admin-theme-text, #333); background: var(--admin-theme-surface, #fff); cursor: pointer; white-space: nowrap; }
  button:hover:not(:disabled) { background: var(--admin-theme-raised, #f0f0f0); }
  button:disabled { color: var(--admin-theme-muted, #949494); background: var(--admin-theme-raised, #f4f4f4); cursor: default; }
  :is(button, input, select, [tabindex]):focus-visible { outline: 2px solid #7253e7; outline-offset: 2px; }
  input, select { box-sizing: border-box; border: 1px solid var(--admin-theme-border, #d6d6d6); border-radius: 3px; padding: 7px 8px; color: var(--admin-theme-text, #333); background: var(--admin-theme-surface, #fff); min-height: 33px; }
  input:disabled, select:disabled { background: var(--admin-theme-raised, #f7f7f7); color: var(--admin-theme-muted, #626262); }
  input[type='checkbox'] { accent-color: var(--admin-theme-text, #292929); width: 16px; min-height: 16px; margin: 0; cursor: pointer; }
  ::selection { background: var(--admin-theme-accent-soft, #e8e1fc); }
  @media (max-width: 720px) { width: calc(100vw - 24px); max-height: calc(100dvh - 24px); }
`;
const Header = styled.header`
  display: flex; justify-content: space-between; align-items: center; padding: 22px 26px; border-bottom: 1px solid var(--admin-theme-border, #e8e8e8);
  h2 { margin: 0; font-size: 20px; font-weight: 600; } button { display: grid; place-items: center; padding: 4px; border: 0; }
`;
const Body = styled.div`
  padding: 20px 26px 24px; max-height: calc(100dvh - 244px); overflow-y: auto; font-size: 13px;
  h3 { font-size: 15px; margin: 28px 0 12px; font-weight: 600; }
  p { line-height: 1.6; } .help { color: var(--admin-theme-muted, #656565); margin: 0 0 16px; }
  .error { color: var(--admin-theme-danger, #b42318); background: var(--admin-theme-danger-soft, #fff0ee); padding: 10px 12px; }
  .notice { color: var(--admin-theme-text, #215d36); background: var(--admin-theme-success-soft, #eef8f0); padding: 10px 12px; }
  .locked { color: var(--admin-theme-warning, #665124); background: var(--admin-theme-warning-soft, #fff8e8); padding: 10px 12px; }
  .empty { padding: 50px 16px; text-align: center; color: var(--admin-theme-muted, #656565); }
  @media (max-width: 720px) { padding: 16px; max-height: calc(100dvh - 204px); }
`;
const Actions = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin-bottom: 14px;
  strong { margin-right: auto; font-size: 13px; font-weight: 500; }
`;
const Scroll = styled.div`overflow: auto; scrollbar-color: var(--admin-theme-muted, #b8b8b8) #f2f2f2; scrollbar-width: thin;`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; text-align: center; white-space: nowrap; font-size: 12px;
  ${sellerTable}
  th, td { border: 1px solid var(--admin-theme-border, #e5e7eb); }
  td.number { text-align: right; font-variant-numeric: tabular-nums; } td.product { min-width: 140px; text-align: left; }

`;
const Summary = styled.div`
  display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap; gap: 16px; padding-top: 14px; font-variant-numeric: tabular-nums;
  span { color: var(--admin-theme-muted, #666); } strong { font-weight: 600; } small { width: 100%; text-align: right; color: var(--admin-theme-muted, #666); }
`;
const Footer = styled.footer`
  display: flex; justify-content: center; padding: 18px 26px 22px; border-top: 1px solid var(--admin-theme-border, #e8e8e8);
  button.complete { min-width: 180px; min-height: 40px; color: white; background: linear-gradient(135deg, #8454f5, #4211c6); border: 0; border-radius: 6px; }
  button.complete:hover:not(:disabled) { filter: brightness(.94); } button.complete:disabled { color: var(--admin-theme-muted, #949494); background: var(--admin-theme-raised, #eee); }
`;

const CancelReasonBox = styled.section`padding: var(--space-4); margin-block: var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md);`;

const ReadOnlyText = styled.div<{ $wide: boolean }>`
  width: ${props => props.$wide ? '15rem' : '8rem'};
  text-align: left;
`;

const StatusButton = styled.button`
  &&[data-tone='brand'] {
    color: var(--color-text-on-brand);
    background: var(--gradient-brand);
    border-color: var(--color-brand);
    font-weight: var(--font-weight-medium);
  }
  &&[data-tone='brand']:hover:not(:disabled) { background: var(--gradient-brand-hover); }
  &&[data-tone='danger'] {
    color: var(--color-danger-strong);
    background: var(--color-danger-subtle);
    border-color: var(--color-danger);
  }
  &&[data-tone='warning'] {
    color: var(--color-warning);
    background: var(--color-warning-subtle);
    border-color: var(--color-warning);
  }
  &&:is([data-tone='danger'], [data-tone='warning']):hover:not(:disabled) {
    box-shadow: inset 0 0 0 1px currentColor;
  }
  &&:focus-visible { outline-color: var(--color-focus-ring); }
  &&[data-tone]:disabled {
    color: var(--color-text-disabled);
    background: var(--color-bg-muted);
    border-color: var(--color-border-default);
    border-style: solid;
    box-shadow: none;
    cursor: not-allowed;
  }
`;
